import bfgOnboarding from './assets/images/bfg-2026/onboarding.svg';
import bfgStepper from './assets/images/bfg-2026/stepper.svg';
import goodPlanetLogo from './assets/images/goodplanet-logo.jpeg';
import mavOnboarding from './assets/images/mai-a-velo/onboarding.png';
import mavStepper from './assets/images/mai-a-velo/stepper.svg';
import mavLogo from './assets/images/mai-a-velo-logo.png';
/**
 * `event.code` is always `${family}_${year}` (eg. `mav_2026`). `bttf` is a
 * one-off legacy code (no year) for an archived campaign and is intentionally
 * never returned here — it's handled as an isolated exact-code exception
 * wherever it's still relevant (see eventHasHomeTab/eventHasFaqTab below, and
 * event-dialog.tsx / bring-together.tsx in the companies app).
 */
export function getEventFamily(code) {
  const family = code?.toLowerCase().split('_')[0];
  return family === 'bfg' || family === 'mav' ? family : undefined;
}
/**
 * Assets/copy tied to one specific edition (`event.code`, eg. `mav_2026`).
 * These are genuinely new every year (new illustration files, new HR kit
 * link...), so each new edition needs its own entry here.
 */
export const campaignEditions = {
  mav_2026: {
    family: 'mav',
    stepperImage: mavStepper,
    illustration: mavOnboarding,
    panelBackgroundColor: '#fefcf0'
  },
  mav_2027: {
    family: 'mav',
    familyConfigOverrides: {
      hasStatsTab: true,
      hasPotentialTab: true
    }
  },
  bfg_2026: {
    family: 'bfg',
    stepperImage: bfgStepper,
    illustration: bfgOnboarding,
    panelBackgroundColor: 'transparent',
    panelOverflowHidden: true,
    panelWidth: 'auto',
    sitesCopy: {
      titleKey: 'companies.pages.new-company.sites.bfg_title',
      descriptionKey: 'companies.pages.new-company.sites.bfg_description',
      autofillDescriptionKey: 'companies.pages.new-company.sites.autofill_card.bfg_description'
    },
    hrKitUrl: 'https://drive.google.com/drive/folders/1OHenVwyCjJu1NjB1p6fNSNs8QIdI1pRp'
  }
};
export function getCampaignEdition(code) {
  return code ? campaignEditions[code.toLowerCase()] : undefined;
}
/**
 * Behavior flags that apply to a whole family regardless of year — these are
 * inherited automatically when a new edition of a known family shows up (eg.
 * `bfg_2027`), no change needed here unless the behavior itself changes.
 */
export const campaignFamilies = {
  mav: {
    coOrganizer: {
      name: 'Mai à Vélo',
      logo: mavLogo
    },
    hasDedicatedDonationCopy: true,
    hasDrawerHighlight: true,
    hasFaqDescriptionInCard: false,
    hasFaqLinkInPointsDialog: false,
    hasFaqTab: false,
    hasHomeTab: true,
    hasLeaderboardScoreFutureCard: false,
    hasLotsTab: false,
    hasParticipantsBlock: false,
    hasPotentialTab: false,
    hasReminderDialog: false,
    hasRideReminder: true,
    hasStatsTab: false,
    showCompanyParticipantsCount: false,
    solidaritySubFeatures: [{
      icon: '❤️',
      titleKey: 'donation_mav'
    }, {
      icon: '🏫',
      titleKey: 'school_support'
    }]
  },
  bfg: {
    coOrganizer: {
      name: 'GoodPlanet',
      logo: goodPlanetLogo
    },
    hasDedicatedDonationCopy: true,
    hasDrawerHighlight: true,
    hasFaqDescriptionInCard: true,
    hasFaqLinkInPointsDialog: true,
    hasFaqTab: true,
    hasHomeTab: true,
    hasLeaderboardScoreFutureCard: true,
    hasLotsTab: true,
    hasParticipantsBlock: true,
    hasPotentialTab: true,
    hasReminderDialog: true,
    hasRideReminder: false,
    hasStatsTab: true,
    showCompanyParticipantsCount: true,
    solidaritySubFeatures: [{
      icon: '❤️',
      titleKey: 'donation_bfg'
    }]
  }
};
export function getCampaignFamilyConfig(code) {
  const family = getEventFamily(code);
  if (!family) return undefined;
  return {
    ...campaignFamilies[family],
    ...getCampaignEdition(code)?.familyConfigOverrides
  };
}

// bttf is a legacy, archived campaign — not part of the family model above,
// kept only as an isolated exact-code exception for the two tabs it needs.
export function eventHasHomeTab(code) {
  return code?.toLowerCase() === 'bttf' || !!getCampaignFamilyConfig(code)?.hasHomeTab;
}
export function eventHasFaqTab(code) {
  return code?.toLowerCase() === 'bttf' || !!getCampaignFamilyConfig(code)?.hasFaqTab;
}
export function eventHasStatsTab(code) {
  return !!getCampaignFamilyConfig(code)?.hasStatsTab;
}
export function eventHasPotentialTab(code) {
  return !!getCampaignFamilyConfig(code)?.hasPotentialTab;
}
export function eventHasLotsTab(code) {
  return !!getCampaignFamilyConfig(code)?.hasLotsTab;
}